/**
 * Typed analytics wrapper around posthog-js.
 *
 * - Initializes lazily when VITE_POSTHOG_KEY is present.
 * - No-ops cleanly when the key is missing (preview, local dev, contributors).
 * - Event names are union-typed so we can't drift into stringly-typed chaos.
 *
 * Funnel north star:
 *   landing_view → niche_detail_view → signup_completed
 *   → onboarding_completed → design_generated → publish_clicked
 */

// posthog-js is loaded lazily (dynamic import) so it never blocks LCP.
type PostHogModule = typeof import("posthog-js").default;
let posthog: PostHogModule | null = null;

type PlatformId =
  | "etsy"
  | "amazon_merch"
  | "redbubble"
  | "teepublic"
  | "society6"
  | "zazzle"
  | "spreadshirt"
  | "threadless"
  | "displate";

type EventMap = {
  // Public surfaces
  landing_view: { variant?: string };
  landing_top_niche_click: { slug: string; demand: number };
  landing_browse_index_click: Record<string, never>;
  free_finder_view: Record<string, never>;
  free_finder_search: { query: string; results_count: number };
  free_finder_result_click: { slug: string; query: string };
  free_finder_signup_click: { ref: string };
  // Amazon Merch niche finder (/tools/amazon-merch-niche-finder)
  merch_finder_view: Record<string, never>;
  merch_finder_search: { query: string; results_count: number };
  merch_finder_select: { slug: string };
  merch_finder_signup_click: { ref: string };
  merch_finder_copy_listing: { slug: string | null };
  pricing_view: Record<string, never>;
  blog_view: { slug?: string };
  niche_index_view: { count?: number; category?: string | null };
  category_hub_view: { slug: string; name: string; total_niches: number };
  intent_hub_view: { slug: "trending" | "low-saturation" | "beginners"; total_niches: number };
  niche_detail_view: {
    slug: string;
    name: string;
    category: string | null;
    demand_score: number;
    saturation_score: number;
    has_seo_content: boolean;
  };
  // Auth funnel
  signup_started: { ref?: string | null; mode?: string | null };
  signup_completed: { ref?: string | null; method?: "email" | "google" };
  login_completed: { method?: "email" | "google" };
  // Onboarding
  onboarding_step_view: { step: number; name: string };
  onboarding_completed: Record<string, never>;
  // Workspace activation
  design_generated: { style?: string; niche?: string };
  design_downloaded: { format?: string };
  tags_generated: { platform?: PlatformId };
  kit_generated: { platforms_count?: number };
  publish_clicked: { platform: PlatformId };
  // Monetization
  checkout_started: { plan?: string };
  out_of_credits_modal_view: { reason?: string };
};

let initialized = false;
let enabled = false;
const queue: Array<() => void> = [];

// PostHog project key is a publishable identifier (prefix `phc_`) — safe in client code.
const DEFAULT_POSTHOG_KEY = "phc_zmZHdz9NXQcxTNJhLS2SRwgzT3i3e3xWfbXhFzns5VUS";

function getKey(): string | undefined {
  return (import.meta.env['VITE_POSTHOG_KEY'] as string | undefined) ?? DEFAULT_POSTHOG_KEY;
}

/**
 * Public marketing routes don't need session replay, surveys, or dead-click
 * autocapture — those alone add ~95 KB of blocking JS to the LCP path.
 * Recording is enabled only inside the authenticated app shell.
 */
function isPublicRoute(): boolean {
  if (typeof window === "undefined") return true;
  const p = window.location.pathname;
  return !(p.startsWith("/app") || p.startsWith("/onboarding"));
}

async function loadAndInit() {
  if (enabled) return;
  const key = getKey();
  if (!key) return;
  const mod = await import("posthog-js");
  posthog = mod.default;
  const publicRoute = isPublicRoute();
  posthog.init(key, {
    api_host: "https://eu.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: !publicRoute,
    autocapture: false,
    disable_session_recording: publicRoute,
    disable_surveys: publicRoute,
    // Prevents the SDK from pulling recorder.js / surveys.js / dead-clicks.js
    // as side-effect dependencies on the LCP path.
    disable_external_dependency_loading: publicRoute,
  } as Parameters<PostHogModule["init"]>[1]);
  enabled = true;
  // Flush any tracks queued before init resolved.
  while (queue.length) queue.shift()!();
}

export function initAnalytics() {
  if (initialized) return;
  initialized = true;
  if (!getKey()) {
    if (import.meta.env.DEV) console.info("[analytics] disabled — VITE_POSTHOG_KEY not set");
    return;
  }
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isPreview =
    host.includes("id-preview--") ||
    host.includes("lovableproject.com") ||
    host.includes("lovable.app");
  if (isPreview && !import.meta.env.PROD) return;

  // Defer until the browser is idle so PostHog never competes with the
  // initial render. Falls back to a 2 s timeout for browsers without rIC.
  const schedule =
    (typeof window !== "undefined" && (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback) ||
    ((cb: () => void) => setTimeout(cb, 2000));
  schedule(() => { void loadAndInit(); }, { timeout: 4000 });
}

export function track<K extends keyof EventMap>(event: K, props?: EventMap[K]) {
  if (!enabled) {
    if (initialized) queue.push(() => posthog?.capture(event, props as Record<string, unknown> | undefined));
    return;
  }
  posthog?.capture(event, props as Record<string, unknown> | undefined);
}

export function identify(userId: string, traits?: Record<string, unknown>) {
  if (!enabled) {
    queue.push(() => posthog?.identify(userId, traits));
    return;
  }
  posthog?.identify(userId, traits);
}

export function resetAnalytics() {
  if (!enabled) return;
  posthog?.reset();
}
